/* eslint-disable default-case */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Box from '../../elements/Box';
import TravelClassFilter from './TravelClassFilterV2';
import FlightTypeFilter from './FlightTypeFilterV2';
import FlightStopCountFilter from './FlightStopCountFilterV2';
import RedemptionUnitFilter from './RedemptionFilterV2';
import { Button } from '@front10/base-elements';

import { useMobileFilter } from '../../lab/FlexibleFilterContainer';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import Text from '../../elements/Text';
import { clientHeight, isNotNil } from '../../../helpers/utils';
import { useIsMobileView } from '@front10/utils';
import {
  MobileBody,
  MobileFilterDialog,
  MobileFooter,
  MobileHeader
} from '../../lab/MobileFilterView';
import WrapperButton from './WrapperButton';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';

const SecondaryFiltersCollapsed = (
  {
    filter,
    containerSettings,
    containerFiltersSetting,
    filterButtonIconProps,
    headerStyle,
    availableFilters,
    travelClass,
    clearAllColumnSettings,
    clearAllExternalColumnSettings,
    clearAllSettings,
    showClearButton,
    onClearFiltersSecondary,
    travelClassColumnSettings,
    travelClassSettings,
    flightType,
    flightTypeSettings,
    flightTypeColumnSettings,
    flightStopCount,
    flightStopCountSettings,
    flightStopCountColumnSettings,
    redemptionUnit,
    redemptionUnitColumnSettings,
    redemptionUnitSettings,
    virtualListHeightSubtraction = 120,
    loading,
    displayFooter = true,
    enabledDoneButton = true,
    enabledClearButton = true,
    toggleButtonSettings,
    ...rest
  },
  ref
) => {
  const {
    translate,
    direction,
    theme: { designTokens }
  } = React.useContext(Context);

  const mobileTitle = translate('cmp-secondary-filter-collapsed-title', 'Filters');
  const labelBottomBtn = translate('cmp-secondary-filter-collapsed-done-button', 'Done');
  const labelClearBtn = translate('cmp-secondary-filter-collapsed-clear-button', 'Reset');
  const labelClearAllBtn = translate('cmp-secondary-filter-clear-all-button', 'Reset');
  let isMobile = useIsMobileView();

  if (rest.mobileView) {
    isMobile = true;
  }

  const dialog = useMobileFilter({
    onSave: () => {
      if (filterState.current.flightType !== flightType.value) {
        flightType.onChange({ value: filterState.current.flightType });
      }
      if (
        filterState.current.travelClass !==
        (Array.isArray(travelClass.value) ? travelClass.value[0] : travelClass.value)
      ) {
        travelClass.onChange({ value: filterState.current.travelClass });
      }
      if (filterState.current.flightStopCount !== flightStopCount.value) {
        flightStopCount.onChange({ value: filterState.current.flightStopCount });
      }
      if (filterState.current.redemptionUnit !== redemptionUnit.value) {
        redemptionUnit.onChange({ value: filterState.current.redemptionUnit });
      }
    },
    onDismiss: () => {
      filterState.current = {
        flightType: flightType.value,
        travelClass: Array.isArray(travelClass.value) ? travelClass.value[0] : travelClass.value,
        flightStopCount: flightStopCount.value,
        redemptionUnit: redemptionUnit.value
      };
    }
    // onReset: props.onReset,
    // onOpen: props.onOpen
  });

  const collapseFilters = React.useMemo(() => isMobile && availableFilters.length > 1, [
    availableFilters.length,
    isMobile
  ]);

  const mobileOrientation = React.useMemo(() => (collapseFilters ? 'vertical' : 'horizontal'), [
    collapseFilters
  ]);

  const filterState = React.useRef({
    flightType: flightType.defaultValue,
    travelClass: travelClass.defaultValue,
    flightStopCount: flightStopCount.defaultValue,
    redemptionUnit: redemptionUnit.defaultValue
  });

  const filtersChanged = React.useMemo(() => {
    const filtersChange = !!(
      travelClass.isChanged ||
      flightType.isChanged ||
      flightStopCount.isChanged ||
      redemptionUnit.isChanged ||
      filterState.current.flightType !== flightType.value ||
      filterState.current.travelClass !==
        (Array.isArray(travelClass.value) ? travelClass.value[0] : travelClass.value) ||
      filterState.current.redemptionUnit !== redemptionUnit.value ||
      filterState.current.flightStopCount !== flightStopCount.value
    );
    return filtersChange;
  }, [
    travelClass.isChanged,
    travelClass.value,
    flightType.isChanged,
    flightType.value,
    flightStopCount.isChanged,
    flightStopCount.value,
    redemptionUnit.isChanged,
    redemptionUnit.value
  ]);

  const allFiltersChanged = React.useMemo(() => {
    let filtersChange = !!(
      travelClass.isChanged ||
      flightType.isChanged ||
      flightStopCount.isChanged ||
      redemptionUnit.isChanged ||
      isNotNil(rest.budget?.value) ||
      !!(rest.dateRange?.value.departureDate || rest.dateRange?.value.returnDate) ||
      !!(rest.locations?.origin.value || rest.locations?.destination.value)
    );

    return filtersChange;
  }, [
    travelClass.isChanged,
    flightType.isChanged,
    flightStopCount.isChanged,
    redemptionUnit.isChanged,
    rest.locations.origin.value,
    rest.locations.destination.value,
    rest.dateRange?.value?.departureDate,
    rest.dateRange?.value?.returnDate,
    rest.budget.value
  ]);

  const controlBtnRef = React.useRef(null);

  const elementsWrappers = {
    FlightType: (e) => (
      <Box
        key="FlightType"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...flightTypeColumnSettings}
      >
        {e}
      </Box>
    ),
    TravelClass: (e) => (
      <Box
        key="TravelClass"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...travelClassColumnSettings}
      >
        {e}
      </Box>
    ),
    FlightStopCount: (e) => (
      <Box
        key="FlightStopCount"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...flightStopCountColumnSettings}
      >
        {e}
      </Box>
    ),
    RedemptionUnit: (e) => (
      <Box
        key="RedemptionUnit"
        marginRight="8px"
        marginBottom="12px"
        floatLeft
        {...redemptionUnitColumnSettings}
      >
        {e}
      </Box>
    )
  };

  const elements = {
    FlightType: (
      <FlightTypeFilter
        mobileOrientation={mobileOrientation}
        showTitle={collapseFilters}
        {...flightTypeSettings}
        {...flightType}
        onChange={(value) => {
          collapseFilters
            ? (filterState.current.flightType = value.value)
            : flightType.onChange(value);

          if (isMobile && controlBtnRef.current?.isDisabled()) {
            controlBtnRef.current?.disable(false);
          }
        }}
      />
    ),
    TravelClass: (
      <TravelClassFilter
        mobileOrientation={mobileOrientation}
        showTitle={collapseFilters}
        {...travelClassSettings}
        {...travelClass}
        onChange={(value) => {
          collapseFilters
            ? (filterState.current.travelClass = value.value)
            : travelClass.onChange(value);

          if (isMobile && controlBtnRef.current?.isDisabled()) {
            controlBtnRef.current?.disable(false);
          }
        }}
      />
    ),
    FlightStopCount: (
      <FlightStopCountFilter
        mobileOrientation={mobileOrientation}
        showTitle={collapseFilters}
        {...flightStopCountSettings}
        {...flightStopCount}
        onChange={(value) => {
          collapseFilters
            ? (filterState.current.flightStopCount = value.value)
            : flightStopCount.onChange(value);

          if (isMobile && controlBtnRef.current?.isDisabled()) {
            controlBtnRef.current?.disable(false);
          }
        }}
      />
    ),
    RedemptionUnit: (
      <RedemptionUnitFilter
        mobileOrientation={mobileOrientation}
        showTitle={collapseFilters}
        {...redemptionUnitSettings}
        {...redemptionUnit}
        onChange={(value) => {
          collapseFilters
            ? (filterState.current.redemptionUnit = value.value)
            : redemptionUnit.onChange(value);

          if (isMobile && controlBtnRef.current?.isDisabled()) {
            controlBtnRef.current?.disable(false);
          }
        }}
      />
    )
  };

  const buttonLabel = translate('cmp-filter-collapsed-button', 'More filters');

  const toggleElement = (
    <FilterButton
      iconSettings={filterButtonIconProps}
      value={buttonLabel}
      aria-label={`More Filter`}
      active={toggleButtonSettings?.activeButton ?? filtersChanged}
      disabled={rest.disabled} // used to disabled the component
    />
  );

  const toggleButton = isMobile
    ? React.cloneElement(toggleElement, { onClick: dialog.open })
    : toggleElement;

  const ClearButton = () => (
    <Box floatLeft {...clearAllColumnSettings}>
      <Button
        onClick={handleClear}
        disabled={!filtersChanged}
        cursor={!filtersChanged ? 'default' : 'pointer'}
        dataTestId="clearAllBtn"
        ariaLabel={translate('cmp-secondary-filter-clear-button-accessibility-message', 'Clear')}
        {...clearAllSettings}
      >
        {labelClearBtn}
      </Button>
    </Box>
  );

  /**
   * It is necessary to create the payload of only the active filters,
   * because clearAll only affects the secondary filters, never the primary ones.
   *
   * IMPORTANT: As new filters are added, the way in which said
   * filters are cleaned must also be added.
   */
  const handleClear = (clearAll = false) => {
    setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    if (clearAll) {
      const newPayload = {
        origin: {},
        destination: {},
        dateRange: {},
        budget: null,
        tripDuration: null,
        rating: null,
        travelerInterest: {},
        airlines: null
      };

      onClearFiltersSecondary(newPayload);
    } else {
      onClearFiltersSecondary({});
      filterState.current = {
        flightType: flightType.defaultValue,
        travelClass: travelClass.defaultValue,
        flightStopCount: flightStopCount.defaultValue,
        redemptionUnit: redemptionUnit.defaultValue
      };

      if (!controlBtnRef.current.isDisabled()) {
        controlBtnRef.current?.disable(true);
      }
    }
  };

  const firstElementRef = useRef(null);

  React.useImperativeHandle(ref, () => ({
    focusFirstElement: () => {
      setTimeout(() => firstElementRef.current?.focusToggleElement(), 100);
    }
  }));

  const MobileView = (props) => (
    <React.Fragment>
      {toggleButton}
      <MobileFilterDialog label={mobileTitle} onDismiss={dialog.dismiss} isVisible={dialog.isOpen}>
        <MobileHeader
          title={mobileTitle ?? ''}
          onBackButtonPress={() => {
            dialog.dismiss();
          }}
        />
        <MobileBody id="secondaryFilterCollapsedMobileBody">{props.children}</MobileBody>
        {rest.footer
          ? rest.footer
          : displayFooter && (
              <MobileFooter align="end">
                {enabledClearButton && (
                  <WrapperButton
                    ref={controlBtnRef}
                    disabled={!filtersChanged}
                    variant="text"
                    onClick={handleClear}
                  >
                    {labelClearBtn || 'Clear'}
                  </WrapperButton>
                )}

                <Button disabled={false} variant="text" onClick={dialog.save}>
                  {labelBottomBtn || 'Done'}
                </Button>
              </MobileFooter>
            )}
      </MobileFilterDialog>
    </React.Fragment>
  );

  const Container = ({ children }) => (
    <Box
      display="inline-block"
      cmp-styles={{
        height: isMobile
          ? `${clientHeight() - (virtualListHeightSubtraction ?? 120)}px`
          : undefined,
        overflow: isMobile ? 'auto' : undefined,
        width: isMobile ? '100%' : undefined
      }}
      {...containerFiltersSetting}
    >
      {children}
    </Box>
  );

  const WrapperFilters = ({ children }) => (
    <Container>
      {children}
      {showClearButton && <ClearButton />}
    </Container>
  );

  const Filters = availableFilters.map((ele, index) => {
    let e = ele;
    if (typeof e === 'object') {
      e = e.type;
    }
    const wrapper = elementsWrappers[e];
    const element = wrapper(elements[e]);

    if (index === 0) {
      const temporalElement = React.cloneElement(elements[e], { ref: firstElementRef });
      return wrapper(temporalElement);
    } else {
      return element;
    }
  });

  if (isMobile) {
    return (
      <EmotionCache direction={direction}>
        <ThemeProvider theme={designTokens}>
          <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
            <Box display="inline-block" {...containerFiltersSetting}>
              {collapseFilters ? (
                <MobileView>
                  <Container>
                    <Text
                      type="p"
                      textAlign="left"
                      variant="text.fontSize7"
                      paddingLeft="20px"
                      paddingRight="20px"
                      textColor="border"
                      fontSize="12px"
                      lineHeight="18px"
                      {...headerStyle}
                    >
                      {collapseFilters
                        ? translate(
                            'cmp-secondary-collapsed-header',
                            '* Fare displayed have been collected within the last 48hrs and may no longer be available at time of booking. Learn more about this offer. Additional fees and charge for optional products and services may be apply'
                          )
                        : null}
                    </Text>
                    <Panels showSeparator={false}>{Filters}</Panels>
                  </Container>
                </MobileView>
              ) : (
                Filters
              )}
              {showClearButton && (
                <Box floatLeft {...clearAllExternalColumnSettings}>
                  <Button
                    disabled={!allFiltersChanged}
                    variant="ghost"
                    paddingY={0}
                    sx={{
                      height: '32px'
                    }}
                    ariaLabel={translate(
                      'cmp-secondary-filter-clear-all-button-accessibility-message',
                      'Clear'
                    )}
                    onClick={() => handleClear(true)}
                  >
                    {labelClearAllBtn || 'Clear'}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </ThemeProvider>
      </EmotionCache>
    );
  }

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <Box className="fr-secondary-filters" display="flex" {...containerSettings}>
          <WrapperFilters>{Filters}</WrapperFilters>
        </Box>
      </ThemeProvider>
    </EmotionCache>
  );
};

SecondaryFiltersCollapsed.propTypes = {
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(
    PropTypes.oneOf([
      'Passengers/TravelClass',
      'TravelClass',
      'FlightType',
      'Route',
      'FlightStopCount',
      'RedemptionUnit',
      PropTypes.objectOf(PropTypes.any)
    ])
  ),
  virtualListHeightSubtraction: PropTypes.number,
  containerSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  travelClassSettings: PropTypes.objectOf(PropTypes.any),
  travelClassColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightStopCountSettings: PropTypes.objectOf(PropTypes.any),
  flightStopCountColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightStopCount: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  redemptionUnitSettings: PropTypes.objectOf(PropTypes.any),
  redemptionUnitColumnSettings: PropTypes.objectOf(PropTypes.any),
  redemptionUnit: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  travelClass: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  flightType: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  onClear: PropTypes.func,
  loading: PropTypes.bool
};

SecondaryFiltersCollapsed.defaultProps = {
  loading: false,
  availableFilters: ['FlightType'],
  showLabels: false,
  containerSettings: {},
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    borderNone: true
  },
  clearAllColumnSettings: {},
  originDestinationSettings: {},
  travelClassSettings: {},
  travelClassColumnSettings: {},
  flightTypeSettings: {},
  flightTypeColumnSettings: {},
  sortingRowSettings: {},
  locations: { origin: {}, destination: {} },
  travelClass: {},
  flightType: {},
  flightStopCountSettings: {},
  flightStopCountColumnSettings: {},
  flightStopCount: {},
  redemptionUnitSettings: {},
  redemptionUnitColumnSettings: {},
  redemptionUnit: {},
  onClear: null,
  virtualListHeightSubtraction: 120
};

export default React.forwardRef(SecondaryFiltersCollapsed);
